import { createLogger } from "./logger";

const logger = createLogger("ChunkReloadRecovery");

/**
 * Recovers a Vite SPA from a failed JS module / `modulepreload` fetch on boot.
 *
 * Why this exists: our Vite apps (onboarding, menu, checkout) ship a single
 * entry chunk plus several `modulepreload`'d vendor chunks. First load is often
 * a fresh cross-origin navigation (e.g. auth → onboarding after email
 * verification), which fires all of these fetches in parallel. If ANY one drops
 * — a transient blip on a phone / corporate-proxied network, or an edge hiccup
 * while a deploy propagates — the boot wedges permanently on the loading shell:
 * there is no retry, and a network preload failure never reaches React's error
 * boundary (it's not a render error). The user is stuck until they manually
 * hard-refresh. (Incident: Squeaky Wizard onboarding, 2026-06-17.)
 *
 * This installs the canonical recovery: listen for Vite's `vite:preloadError`
 * (and, as a backstop, a failed entry `<script type="module">` / modulepreload
 * `<link>` resource error) and do a SINGLE guarded `location.reload()`. The
 * reload re-fetches the modules and almost always succeeds. A sessionStorage
 * guard prevents a reload loop when an asset is genuinely gone (e.g. a chunk
 * removed by a deploy and not in cache) — after one attempt we stop and let the
 * existing error UI take over.
 *
 * Call once, as early as possible in the app entry (before `createRoot`).
 */
export interface ChunkReloadRecoveryOptions {
  /**
   * Best-effort reporter invoked just before the recovery reload. Apps pass a
   * closure over their already-initialized Sentry instance so we can measure
   * how often this fires in production. Kept as a callback so `@order-up/client`
   * takes no Sentry dependency. Errors thrown here are swallowed.
   */
  onRecover?: (info: { url: string; reason: ChunkReloadReason }) => void;
}

export type ChunkReloadReason = "vite-preload-error" | "module-resource-error";

const RELOAD_GUARD_KEY = "__ou_chunk_reload__";
/** Don't reload again if we already tried within this window (ms). */
const RELOAD_GUARD_WINDOW_MS = 10_000;

let installed = false;

export function installChunkReloadRecovery(
  options: ChunkReloadRecoveryOptions = {},
): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  let recovering = false;

  const recover = (url: string, reason: ChunkReloadReason): void => {
    // Only act once per page load — multiple chunks can fail together.
    if (recovering) return;
    recovering = true;

    if (recentlyReloaded()) {
      logger.error(
        "Module fetch failed and a recovery reload was already attempted — not reloading again (asset is likely gone).",
        { url, reason },
      );
      // Leave `recovering` true: fall through to the app's own error UI.
      return;
    }

    try {
      options.onRecover?.({ url, reason });
    } catch {
      // best-effort telemetry — never block the reload
    }

    logger.warn("Module fetch failed on boot — reloading once to recover.", {
      url,
      reason,
    });
    markReloaded();
    window.location.reload();
  };

  // 1) Vite's first-class signal for a failed dynamic import / modulepreload.
  //    `preventDefault()` suppresses the default rethrow so the console isn't
  //    spammed with an uncaught error before the reload.
  window.addEventListener("vite:preloadError", (event: Event) => {
    event.preventDefault();
    const payload = (event as Event & { payload?: unknown }).payload;
    recover(resolveUrl(payload), "vite-preload-error");
  });

  // 2) Backstop: a failed entry `<script type="module">` or modulepreload
  //    `<link>`. Resource error events don't bubble, so capture-phase is
  //    required. Filter to module-ish resources and ignore runtime JS errors
  //    (which carry `error`/`message`, never an element `target`).
  window.addEventListener(
    "error",
    (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target || !isModuleResource(target)) return;
      const url =
        (target as HTMLScriptElement).src ||
        (target as HTMLLinkElement).href ||
        "unknown";
      recover(url, "module-resource-error");
    },
    true,
  );
}

function isModuleResource(el: HTMLElement): boolean {
  if (el instanceof HTMLScriptElement) {
    return (el.type === "module" || /\.m?js(\?|$)/.test(el.src)) && isOwnAsset(el.src);
  }
  if (el instanceof HTMLLinkElement) {
    return el.rel === "modulepreload" && isOwnAsset(el.href);
  }
  return false;
}

/**
 * Only this app's own bundle can be recovered by a reload. A THIRD-PARTY
 * script that fails (reCAPTCHA's `enterprise.js` under a content blocker)
 * fails again after the reload, and the reload itself is the damage: on
 * checkout it landed mid hand-off, re-sent the one-time code and showed the
 * guest "This checkout link has expired" (prod 2026-10-12).
 */
function isOwnAsset(url: string): boolean {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

function resolveUrl(payload: unknown): string {
  if (payload && typeof payload === "object") {
    const maybe = payload as { message?: unknown };
    if (typeof maybe.message === "string") return maybe.message;
  }
  return "unknown";
}

function recentlyReloaded(): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_GUARD_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < RELOAD_GUARD_WINDOW_MS;
  } catch {
    // sessionStorage can throw (private mode / blocked storage). Without a
    // guard a reload loop is possible, so treat "can't read" as "already
    // tried" to stay safe rather than risk looping.
    return true;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // ignore — see recentlyReloaded()
  }
}
